/**
 * Expression Security Validation
 *
 * Pre-flight validation for expressions before parsing/evaluation.
 * Detects dangerous patterns and enforces security limits.
 */
import { EXPRESSION_LIMITS, ALLOWED_FUNCTIONS, ALLOWED_CONTEXTS } from './types';
/**
 * Dangerous patterns that should never appear in expressions
 */
const BLOCKED_PATTERNS = [
    // Code execution
    /\beval\s*\(/i,
    /\bFunction\s*\(/i,
    /\bnew\s+Function/i,
    /\bsetTimeout\s*\(/i,
    /\bsetInterval\s*\(/i,
    // DOM/BOM access
    /\bwindow\b/i,
    /\bdocument\b/i,
    /\bglobalThis\b/i,
    /\bself\b/i,
    // Module system
    /\brequire\s*\(/i,
    /\bimport\s*\(/i,
    /\bimport\b/i,
    /\bexport\b/i,
    // Prototype manipulation
    /\b__proto__\b/,
    /\bconstructor\b/,
    /\bprototype\b/,
    // Process/system access
    /\bprocess\b/i,
    /\bglobal\b/i,
    /\bBuffer\b/,
    // Template literals with expressions (potential injection)
    /\$\{/,
    // Assignment operators (expressions should be pure)
    /[^=!<>]=[^=]/,
    /\+=/,
    /-=/,
    /\*=/,
    /\/=/,
    /%=/,
    /\+\+/,
    /--/,
    // Object/Array constructors
    /\bnew\s+/i,
    /\bObject\s*\(/i,
    /\bArray\s*\(/i,
    // Reflection
    /\bReflect\b/,
    /\bProxy\b/,
    // Fetch/network
    /\bfetch\s*\(/i,
    /\bXMLHttpRequest\b/i,
    // Storage
    /\blocalStorage\b/i,
    /\bsessionStorage\b/i,
    // Comments (could hide code)
    /\/\//,
    /\/\*/,
];
/**
 * Validate an expression string for security issues
 */
export function validateExpression(expression) {
    const errors = [];
    // Check length
    if (expression.length > EXPRESSION_LIMITS.MAX_LENGTH) {
        errors.push(`Expression length ${expression.length} exceeds maximum ${EXPRESSION_LIMITS.MAX_LENGTH}`);
    }
    // Check for blocked patterns
    for (const pattern of BLOCKED_PATTERNS) {
        if (pattern.test(expression)) {
            errors.push(`Expression contains blocked pattern: ${pattern.source}`);
        }
    }
    // Check for balanced parentheses/brackets
    const balanceResult = checkBalance(expression);
    if (!balanceResult.valid) {
        errors.push(balanceResult.error);
    }
    // Check for valid identifier usage (basic check)
    const identifierResult = checkIdentifiers(expression);
    errors.push(...identifierResult);
    return {
        valid: errors.length === 0,
        errors,
    };
}
/**
 * Check for balanced delimiters
 */
function checkBalance(expr) {
    const stack = [];
    const pairs = { '(': ')', '[': ']', '{': '}' };
    const opening = new Set(Object.keys(pairs));
    const closing = new Set(Object.values(pairs));
    let inString = false;
    let stringChar = '';
    for (let i = 0; i < expr.length; i++) {
        const char = expr[i];
        // Handle string boundaries
        if ((char === '"' || char === "'") && expr[i - 1] !== '\\') {
            if (!inString) {
                inString = true;
                stringChar = char;
            }
            else if (char === stringChar) {
                inString = false;
                stringChar = '';
            }
            continue;
        }
        if (inString)
            continue;
        if (opening.has(char)) {
            stack.push(pairs[char]);
        }
        else if (closing.has(char)) {
            if (stack.length === 0) {
                return { valid: false, error: `Unmatched closing '${char}' at position ${i}` };
            }
            const expected = stack.pop();
            if (expected !== char) {
                return {
                    valid: false,
                    error: `Mismatched delimiter: expected '${expected}', got '${char}' at position ${i}`,
                };
            }
        }
    }
    if (inString) {
        return { valid: false, error: 'Unterminated string literal' };
    }
    if (stack.length > 0) {
        return { valid: false, error: `Unmatched opening delimiter` };
    }
    return { valid: true };
}
/**
 * Remove string literals from expression for identifier checking
 */
function stripStrings(expr) {
    let result = '';
    let inString = false;
    let stringChar = '';
    for (let i = 0; i < expr.length; i++) {
        const char = expr[i];
        if (!inString && (char === '"' || char === "'")) {
            inString = true;
            stringChar = char;
            result += ' '; // Replace string start with space
        }
        else if (inString) {
            if (char === '\\' && i + 1 < expr.length) {
                // Skip escape sequence
                result += '  ';
                i++;
            }
            else if (char === stringChar) {
                // End of string
                inString = false;
                stringChar = '';
                result += ' ';
            }
            else {
                // Inside string - replace with space to preserve positions
                result += ' ';
            }
        }
        else {
            result += char;
        }
    }
    return result;
}
/**
 * Check for valid identifier patterns
 */
function checkIdentifiers(expr) {
    const errors = [];
    // Strip strings before checking identifiers
    const exprWithoutStrings = stripStrings(expr);
    // Extract potential identifiers (simplified regex)
    const identifierRegex = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g;
    const keywords = new Set([
        'true',
        'false',
        'null',
        'Math',
        'JSON',
        'Array',
        ...ALLOWED_CONTEXTS,
        ...ALLOWED_FUNCTIONS.filter((f) => !f.includes('.')),
    ]);
    let match;
    while ((match = identifierRegex.exec(exprWithoutStrings)) !== null) {
        const identifier = match[1];
        // Skip if it's a known keyword/function
        if (keywords.has(identifier))
            continue;
        // Skip if it's part of a member expression (preceded by .)
        const precedingChar = exprWithoutStrings[match.index - 1];
        if (precedingChar === '.')
            continue;
        // Skip if it's a function name we recognize
        if (ALLOWED_FUNCTIONS.some((f) => f.startsWith(identifier)))
            continue;
        // This is an unknown root identifier
        errors.push(`Unknown identifier "${identifier}" at position ${match.index}. Allowed: ${ALLOWED_CONTEXTS.join(', ')}`);
    }
    return errors;
}
/**
 * Sanitize an expression by removing obviously dangerous content
 * Note: This is a last-resort fallback, not a substitute for validation
 */
export function sanitizeExpression(expression) {
    // Remove any potential HTML/script injection
    let sanitized = expression.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, '');
    // Collapse multiple spaces
    sanitized = sanitized.replace(/\s+/g, ' ').trim();
    return sanitized;
}
/**
 * Check if an expression is safe for evaluation
 * Returns true only if all validation passes
 */
export function isExpressionSafe(expression) {
    const result = validateExpression(expression);
    return result.valid;
}
//# sourceMappingURL=security.js.map